// StreakInfoSheet — shared bottom-sheet explainer for any streak surface
// in the app. Each streak (workout chain, calorie logging, junk-clean,
// fasting goal, etc.) has its own counting rules; the user can tap the
// streak chip wherever it appears to see exactly what counts.
//
// Pass: { open, onClose, title, lines, accent } — lines is an array of
// short strings or {label, value} pairs that render as a definition
// list. Renders via portal at the app root so it sits above tab chrome.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function StreakInfoSheet({ open, onClose, title = "Streak", lines = [], accent = "#f5b342" }) {
  // Lock background scroll while the sheet is open so taps don't drift
  // through to the underlying view (the backdrop swallows them too, but
  // touch-scrolling on iOS can still bleed through without overflow lock).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return createPortal((
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '0 0 max(20px, env(safe-area-inset-bottom))',
        animation: 'streakInfoFade 0.18s ease-out both',
      }}
    >
      <style>{`
        @keyframes streakInfoFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes streakInfoSlide { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 380, margin: '0 16px',
          background: 'linear-gradient(165deg, #0f161e 0%, #0a0d12 100%)',
          border: `1px solid ${accent}55`,
          borderRadius: 22,
          padding: '20px 22px 18px',
          boxShadow: `0 24px 60px rgba(0,0,0,0.5), 0 0 0 0.5px rgba(255,255,255,0.04) inset`,
          color: '#e6e6e6',
          fontFamily: 'inherit',
          animation: 'streakInfoSlide 0.28s cubic-bezier(0.22,1,0.36,1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: `${accent}22`, border: `1px solid ${accent}66`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16,
          }}>🔥</div>
          <div style={{
            fontFamily: 'monospace', fontSize: 11, fontWeight: 800,
            letterSpacing: '0.16em', color: accent, textTransform: 'uppercase',
          }}>{title}</div>
        </div>

        <div style={{
          fontSize: 13, lineHeight: 1.55, color: '#cfcfcf',
          display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18,
        }}>
          {lines.map((l, i) => {
            if (l && typeof l === 'object' && 'label' in l) {
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ color: '#9d9d9d', flex: 1 }}>{l.label}</span>
                  <span style={{ color: '#e6e6e6', fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{l.value}</span>
                </div>
              );
            }
            return <div key={i}>{l}</div>;
          })}
        </div>

        <button onClick={onClose} style={{
          width: '100%', padding: '13px', borderRadius: 12,
          background: accent, border: 'none', color: '#0A0A0E',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
          letterSpacing: '0.04em', cursor: 'pointer',
        }}>Got it</button>
      </div>
    </div>
  ), document.body);
}
